import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { usePayment } from '../../hooks/usePayment';

const PaymentSuccessPage = () => {
  const { listingId, managementToken } = useParams();
  const navigate = useNavigate();
  const { getPaymentStatus, getGuestPaymentIntent } = usePayment();
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      try {
        let data;
        if (managementToken) {
          // Guest payment success
          setIsGuest(true);
          data = await getGuestPaymentIntent(managementToken);
          // console.log('Guest payment data:', data);
        } else if (listingId) {
          // Regular user payment success
          data = await getPaymentStatus(listingId);
          
        }
        setPaymentData(data);
      } catch (error) {
        console.error('Failed to fetch payment status:', error);
      } finally {
        setLoading(false);
      }
    };

    if (listingId || managementToken) {
      fetchPaymentStatus();
    }
  }, [listingId, managementToken, getPaymentStatus, getGuestPaymentIntent]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          {/* Success Icon */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <svg
              className="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Payment Successful!
          </h1>
          
          <p className="text-lg text-gray-600 mb-8">
            Your listing has been published and is now live on 321Vets.
          </p>

          {paymentData && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Listing Details</h2>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Title:</span>
                  <span className="font-medium">{paymentData.post_title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount Paid:</span>
                  <span className="font-medium">${paymentData.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span className="font-medium text-green-600">Completed</span>
                </div>
                {paymentData.management_token && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Management Token:</span>
                    <span className="font-mono text-sm bg-gray-200 px-2 py-1 rounded">
                      {paymentData.management_token}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-800 mb-2">What's Next?</h3>
              <ul className="text-sm text-blue-700 space-y-1 text-left">
                <li>• Your listing is now visible to potential customers</li>
                <li>• You'll receive email notifications for inquiries</li>
                <li>• You can manage your listing using the management link</li>
                <li>• Your listing will expire in 30 days (extendable)</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition-colors"
              >
                Browse Listings
              </Link>
              
              {paymentData?.management_token && (
                <Link
                  to={`/manage-listing/${paymentData.management_token}`}
                  className="bg-gray-600 text-white px-6 py-3 rounded-md hover:bg-gray-700 transition-colors"
                >
                  Manage Listing
                </Link>
              )}
              
              <button
                onClick={() => navigate('/post')}
                className="border border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-50 transition-colors"
              >
                Create Another Listing
              </button>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Need help? Contact our support team at{' '}
              <a href="mailto:support@321vets.com" className="text-blue-600 hover:underline">
                support@321vets.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
